import { api } from '../core/network/api';
import { t } from './i18n/i18n';
import { sanitizeCmsHtmlWithSafeLinks } from './sanitize-html';

interface LegalPage {
  slug: string;
  title: string;
  content: string;
}

/**
 * Reads one legal document over the public CMS route and shows it in an
 * overlay.
 *
 * An overlay rather than a route: `main.ts` has no `/legal/:slug` case, so the
 * footer links were falling through to the default branch and redirecting to
 * /login. Intercepting them here is what makes them work.
 *
 * The content is admin-authored HTML stored WITHOUT server-side sanitisation,
 * so it goes through `sanitizeCmsHtmlWithSafeLinks` before it is ever written
 * into the DOM.
 */
export function openLegalOverlay(slug: string): () => void {
  const el = document.createElement('div');
  el.className = 'fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.dataset.legalOverlay = slug;
  el.innerHTML = `
    <div class="popup-enter w-full sm:max-w-2xl max-h-[88dvh] flex flex-col bg-surface border border-border border-t-2 border-t-gold rounded-t-lg sm:rounded-lg">
      <div class="shrink-0 flex items-start justify-between gap-3 px-4 py-3 border-b border-border">
        <h2 class="font-heading text-gold text-sm" data-legal-title>${t('common.loading')}</h2>
        <button type="button" data-legal-close aria-label="${t('common.close')}"
          class="shrink-0 min-w-11 min-h-11 -mr-2 -mt-2 flex items-center justify-center text-xl leading-none text-text-dim hover:text-text touch-manipulation">&times;</button>
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]
                  text-sm text-text-dim leading-relaxed prose-game" data-legal-body>
        <p class="text-xs text-text-dim">${t('common.loading')}</p>
      </div>
    </div>`;

  let closed = false;
  function close(): void {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    el.remove();
  }
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }

  el.addEventListener('click', (e) => {
    if (e.target === el || (e.target as HTMLElement).closest('[data-legal-close]')) close();
  });
  document.addEventListener('keydown', onKey);
  document.body.appendChild(el);

  void api
    .get<LegalPage>(`/cms/legal/${encodeURIComponent(slug)}`)
    .then((page) => {
      if (closed) return;
      const title = el.querySelector('[data-legal-title]');
      const body = el.querySelector('[data-legal-body]');
      if (title) title.textContent = page.title ?? slug;
      if (body) body.innerHTML = sanitizeCmsHtmlWithSafeLinks(page.content ?? '');
    })
    .catch(() => {
      if (closed) return;
      const title = el.querySelector('[data-legal-title]');
      const body = el.querySelector('[data-legal-body]');
      if (title) title.textContent = t('legal.notAvailable');
      if (body) body.innerHTML = `<p class="text-xs text-text-dim">${t('legal.notPublished')}</p>`;
    });

  return close;
}

/**
 * Intercepts clicks on `/legal/<slug>` anchors anywhere under `root`.
 *
 * `preventDefault()` matters twice over: it stops the browser navigating, and
 * `main.ts`'s global router checks `defaultPrevented` before handling a click,
 * so without it the SPA router would still redirect.
 */
export function mountLegalLinks(root: HTMLElement): () => void {
  function onClick(e: MouseEvent): void {
    const a = (e.target as HTMLElement).closest('a[href^="/legal/"]') as HTMLAnchorElement | null;
    if (!a) return;
    const slug = a.getAttribute('href')!.slice('/legal/'.length).replace(/\/+$/, '');
    if (!slug) return;
    e.preventDefault();
    openLegalOverlay(slug);
  }
  root.addEventListener('click', onClick);
  return () => root.removeEventListener('click', onClick);
}
